import React from "react";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { LOAN_ACCOUNTS_STATUSES, DISPLAY_DATETIME_FORMAT } from "../constants";
import { capitalCase } from "change-case";
import numeral from "numeral";
import "numeral/locales/en-gb";
import { NullableString } from "banco/types/utility";
import { LoanAccountStatus } from "banco/constants/generated/constants";

// format can be
// - $0,0.00 for 2 decimal places, comma and pound sign
// - 0,0 for no decimals
// see http://numeraljs.com/#format
export const formatMoney =
  (val?: number | string, format = "$0,0.00", blankPlaceholder = false) => {
  // you use a dollar sign ($) in the format string to indicate where you would like
  // the currency symbol to appear.
    numeral.defaultFormat(format);
    numeral.locale("en-gb");

    // If the value is a string with commas, remove them before parsing
    const stringVal = typeof val === "string" ? val.replace(/,/g, "") : `${val}`;
    const parsedVal = parseFloat(stringVal);
    if (isNaN(parsedVal)) {
      return blankPlaceholder ? "£" : numeral(0).format();
    }

    return numeral(parsedVal).format();
  };

export const toPence = (value: number): number => Math.round(value * 100);

dayjs.extend(advancedFormat);

export const formatDate = (d: dayjs.ConfigType, format = "Do MMM YYYY") => (
  d ? dayjs(d).format(format) : ""
);
export const formatDateTime = (d: dayjs.ConfigType, include_seconds = true) => (
  d ? dayjs(d).format(include_seconds ? "Do MMM YYYY HH:mm:ss" : "Do MMM YYYY HH:mm") : ""
);

export const formatDateForServer = (d: dayjs.ConfigType | null) => (
  d ? dayjs(d).format("YYYY-MM-DD") : ""
);

export const formatDateTimeText = (d: dayjs.ConfigType) => (
  d ? dayjs(d).format(DISPLAY_DATETIME_FORMAT) : ""
);

export const formatDateTimeUK = (d: dayjs.ConfigType) => (
  d ? dayjs(d).format("DD/MM/YYYY HH:mm:ss") : ""
);

export const formatPhoneNumber = (phone: string | undefined) => {
  if (!phone) {
    return undefined;
  }
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, "");
  // Format the cleaned number
  const match = cleaned.match(/^(\d{5})(\d{3})(\d{3})$/);
  return match ? `${match[1]} ${match[2]} ${match[3]}` : phone;
};

export const urnToRef = (urn: NullableString = "") => {
  if (!urn) { return ""; }
  return urn.indexOf(":") !== -1 ? urn.substring(urn.lastIndexOf(":") + 1) : `${urn}`;
};

export const formatStatus = (status: string) => {
  return (unformattedStatus => {
    switch (unformattedStatus) {
      case LOAN_ACCOUNTS_STATUSES.PENDING_SETUP:
        return "Pending Setup";
      case LoanAccountStatus.PENDING_FUNDS_RELEASE:
        return "Scheduled for payout";
      default:
        return capitalCase(unformattedStatus);
    }
  })(status);
};

export const formattedStatusLabel = (status: string) => (
  <span className={ `label ${window.fb.label_colours[status]}` }> { formatStatus(status) } </span>
);

export const formatProof = (input: string): string => {
  // Remove "_proof" at the end if it exists
  const formattedInput = input.replace(/_proof$/, "");

  // Replace all underscores with spaces and capitalize each word
  const words = formattedInput.split("_");
  const formattedWords = words.map((word) =>
    word.charAt(0).toUpperCase() + word.slice(1)
  );
  return formattedWords.join(" ");
};

export const formatDocumentName = (input: string): string => {
  return input
    .split("_")
    .map((word, index) => index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word)
    .join(" ");
};

export const getDisplayNameByUrn = (urn: string): string => {
  const be = window.fb.staticdata.banco_employees.get_by_urn(urn);
  return be?.display_name ? be.display_name() : urnToRef(urn);
};

// The historical ("Applicant Communication" tab) chat-message author rendering: the author's
// name with the phone number in parentheses when one is known.
export const formatMessageAuthor = (author: string, phoneNumber?: string): string => {
  const trimmedPhoneNumber = (phoneNumber ?? "").trim();
  return trimmedPhoneNumber ? `${author} (${trimmedPhoneNumber})` : author;
};

export const capitalized = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

// Not reusing Address from banco-api-ts as it requires post_town/postcode, but formatAddress
// handles missing fields
export interface AddressFields {
  sub_building_name?: string;
  building_name?: string;
  building_number?: string;
  thoroughfare?: string;
  post_town?: string;
  postcode?: string;
}

// eslint-disable-next-line max-statements
export const formatAddress = (address: AddressFields | undefined): string[] => {
  if (!address) { return []; }

  const lines: string[] = [];

  const firstLineParts = [address.sub_building_name, address.building_name].filter(Boolean);
  if (firstLineParts.length > 0) {
    lines.push(firstLineParts.join(", "));
  }

  const streetParts = [address.building_number, address.thoroughfare].filter(Boolean);
  if (streetParts.length > 0) {
    lines.push(streetParts.join(" "));
  }

  if (address.post_town) { lines.push(address.post_town); }
  if (address.postcode) { lines.push(address.postcode); }

  return lines;
};

export const formatAddressOneLine = (address: AddressFields | undefined): string => {
  return formatAddress(address).join(", ");
};
